import mitt from 'mitt';

const bus = mitt();
const pending = new Map(); // id -> { resolve, reject, timer }
let ready = false;
let webview = null;
const queue = [];

const genId = () => `m_${Date.now()}_${Math.random().toString(16).slice(2)}`;

// ========== 事件表 ==========
// user.save                儲存使用者資訊
// user.get                 取得使用者資訊
// subscriptionInfo.save    儲存訂閱資訊
// subscriptionInfo.get     取得訂閱資訊
// platform.get             取得平台資訊
// rememberAccount.save     儲存記住我的帳號資訊
// rememberAccount.get      取得記住我的帳號資訊
// loginAccount.save        儲存登入帳號UID
// loginAccount.get         取得登入帳號UID
// selectAvatar.open        開啟選擇角色
// childHome.open           開啟孩子首頁
// meta.open                開啟元宇宙
// achievement.open         開啟成就
// game.open                開啟小遊戲
// store.open               開啟商城
// chatRoom.open            開啟聊天室
// chatRoom.open            開啟聊天室
// fcmToken.get             取得 Firebase Cloud Messaging (FCM) Token

function attachIfReady() {
  if (ready) return;
  if (!window.vuplex) return;

  webview = window.vuplex;
  ready = true;

  // 收訊息（Unity -> JS）
  webview.addEventListener('message', (event) => {
    let msg = event.data;
    try { if (typeof msg === 'string') msg = JSON.parse(msg) } catch (_) {}
    if (!msg || typeof msg !== 'object') return;

    // 回覆路由（含錯誤）
    if (msg.id && (msg.type === 'response' || msg.type === 'error')) {
      const entry = pending.get(msg.id);
      if (entry) {
        pending.delete(msg.id);
        clearTimeout(entry.timer);
        if (msg.type === 'error' || msg.error) entry.reject(msg.error || msg.payload || msg);
        else entry.resolve(msg.result ?? msg.payload ?? null);
      }
      return;
    }

    // 事件路由
    if (msg.type) {
      bus.emit(msg.type, msg.payload ?? msg);
    }
  })

  // 衝刷排隊動作
  queue.splice(0).forEach(fn => fn());
}

function init() {
  if (window.vuplex) {
    attachIfReady();
  } else {
    window.addEventListener('vuplexready', attachIfReady, { once: true });
  }
}
init();

function postToUnity(obj) {
  if (!ready || !webview) throw new Error('vuplex not ready');
  // 大多數 Vuplex 版本可直接傳 Object，內部會序列化為 JSON
  if (typeof webview.postMessage === 'function') {
    webview.postMessage(obj);
  } else if (typeof webview.sendMessage === 'function') {
    webview.sendMessage(obj);
  } else {
    // 某些舊版：可改用 ExecuteJavaScript 呼叫 window.vuplex.postMessage(JSON)
    // webview.ExecuteJavaScript($"window.vuplex.postMessage({JSON.stringify(obj)})")
    throw new Error('No supported post API on vuplex instance')
  }
}

function withReady(fn) {
  if (ready) fn();
  else queue.push(fn);
}

// 發送：單向事件（不等回覆）
function send(type, payload) {
  withReady(() => postToUnity({ type, payload }))
}

// 發送：請求/回覆模式（等 Unity 回傳，支援逾時）
function request(type, payload, { timeout = 8000 } = {}) {
  const id = genId();
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      if (pending.has(id)) {
        pending.delete(id)
        reject(new Error(`Request timeout: ${type}`));
      }
    }, timeout);    

    pending.set(id, { resolve, reject, timer });
    withReady(() => {
      try {
        postToUnity({ id, type, payload });
      } catch (e) {
        clearTimeout(timer);
        pending.delete(id);
        reject(e);
      }
    });
  });
}

// 訂閱 Unity 推播事件（如 user.changed）
function on(type, handler) {
  bus.on(type, handler);
  return () => bus.off(type, handler);
}

export default {
  send,        // 單向事件
  request,     // 請求/回覆
  on,          // 訂閱 Unity 推播事件
  off: (t, h) => bus.off(t, h),
  isReady: () => ready,
};
